import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Save } from "lucide-react";
import { useAdminAuth } from "@/context/AdminAuthContext";
import AdminShell from "@/components/admin/AdminShell";
import RequireAdmin from "@/components/admin/RequireAdmin";
import { useAdminI18n } from "@/lib/adminI18n";

const COUNTRY_PRESETS = [
  { code: "LB", en: "Lebanon",  ar: "لبنان",         fr: "Liban" },
  { code: "TR", en: "Turkey",   ar: "تركيا",         fr: "Turquie" },
  { code: "UK", en: "UK",       ar: "بريطانيا",      fr: "Royaume-Uni" },
  { code: "FR", en: "France",   ar: "فرنسا",         fr: "France" },
  { code: "AE", en: "UAE",      ar: "الإمارات",      fr: "EAU" },
  { code: "CN", en: "China",    ar: "الصين",         fr: "Chine" },
  { code: "US", en: "USA",      ar: "الولايات المتحدة", fr: "États-Unis" },
];

const METHODS = ["air", "sea", "standard", "express"];

function newRate() {
  return {
    id: `rate-${Math.random().toString(36).slice(2, 8)}`,
    country: "LB",
    country_label: { en: "Lebanon", ar: "لبنان", fr: "Liban" },
    method: "air",
    product_category: "general",
    price_per_kg_usd: 0,
    eta: "",
    unit: "kg",
    enabled: true,
  };
}

const PRODUCT_CATEGORIES = [
  // Canonical 5
  { id: "general", label: "General" },
  { id: "perfumes", label: "Perfumes" },
  { id: "perfumes_cosmetics", label: "Perfumes & Cosmetics" },
  { id: "cosmetics", label: "Cosmetics" },
  { id: "supplements", label: "Supplements" },
  { id: "electronics", label: "Electronics" },
  // Public website extras (mirrored from rates.js)
  { id: "clothes_turkish_brands", label: "Clothes — Turkish brands" },
  { id: "clothes_intl_brands", label: "Clothes — Intl. brands" },
  { id: "clothes_shoes", label: "Clothes & Shoes" },
  { id: "clothes_shoes_kg", label: "Clothes & Shoes (per kg)" },
  { id: "medical", label: "Medical products" },
  { id: "home", label: "Home products" },
  { id: "toys_stationery", label: "Toys / Stationery" },
  { id: "with_battery", label: "Items with battery" },
];

const UNITS = [
  { id: "kg", label: "per kg" },
  { id: "cbm", label: "per m³ (CBM)" },
];

function AdminShippingPageInner() {
  const { apiFetch } = useAdminAuth();
  const { t } = useAdminI18n();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deliveryFee, setDeliveryFee] = useState(6);
  const [usdToLbp, setUsdToLbp] = useState(89500);
  const [rates, setRates] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch("/admin/shipping-config");
        const data = await res.json();
        if (cancelled) return;
        setDeliveryFee(Number(data.delivery_fee_usd) || 0);
        setUsdToLbp(Number(data.usd_to_lbp) || 0);
        setRates(Array.isArray(data.rates) ? data.rates : []);
      } catch (e) {
        toast.error("Failed to load shipping config");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [apiFetch]);

  const onSave = async () => {
    // Reject empty / dupe ids — the backend also rejects, but a friendly
    // toast is faster than a 400 round-trip.
    const ids = rates.map((r) => r.id?.trim()).filter(Boolean);
    if (ids.length !== rates.length) { toast.error("Every rate row needs an id"); return; }
    if (new Set(ids).size !== ids.length) { toast.error("Rate ids must be unique"); return; }

    setSaving(true);
    try {
      const res = await apiFetch("/admin/shipping-config", {
        method: "PUT",
        body: JSON.stringify({
          delivery_fee_usd: Number(deliveryFee) || 0,
          usd_to_lbp: Number(usdToLbp) || 0,
          rates: rates.map((r) => ({
            ...r,
            price_per_kg_usd: Number(r.price_per_kg_usd) || 0,
            product_category: r.product_category || "general",
            unit: r.unit || "kg",
          })),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Save failed");
      toast.success("Shipping config saved");
    } catch (e) {
      toast.error(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const updateRate = (idx, patch) => {
    setRates((rs) => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const applyCountryPreset = (idx, code) => {
    const preset = COUNTRY_PRESETS.find((p) => p.code === code);
    if (!preset) return;
    updateRate(idx, {
      country: code,
      country_label: { en: preset.en, ar: preset.ar, fr: preset.fr },
    });
  };

  return (
    <AdminShell
      title={t("shipping.title")}
      description={t("shipping.description")}
    >
      {loading ? (
        <div className="grid place-items-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#F37021]" /></div>
      ) : (
        <div className="space-y-8" data-testid="shipping-admin">
          {/* Global fees */}
          <div className="admin-panel p-5">
            <h2 className="mb-4 font-display text-lg font-bold">{t("shipping.globalFees")}</h2>
            <div className="grid sm:grid-cols-2 gap-4 max-w-xl">
              <NumberField
                label={t("shipping.deliveryFee")}
                value={deliveryFee}
                onChange={setDeliveryFee}
                testid="delivery-fee"
                hint={t("shipping.deliveryFeeHint")}
              />
              <NumberField
                label={t("shipping.exchangeRate")}
                value={usdToLbp}
                onChange={setUsdToLbp}
                testid="usd-to-lbp"
                hint={t("shipping.exchangeRateHint")}
              />
            </div>
          </div>

          {/* Rates table */}
          <div className="admin-panel p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-lg font-bold">{t("shipping.tableTitle")}</h2>
              <button
                type="button"
                onClick={() => setRates((rs) => [...rs, newRate()])}
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-[#F37021] hover:bg-[#E06015] text-xs font-semibold"
                data-testid="add-rate"
              >
                <Plus className="w-3.5 h-3.5" /> {t("shipping.addRate")}
              </button>
            </div>

            {rates.length === 0 ? (
              <p className="text-sm text-white/40 py-6 text-center">
                {t("shipping.noRates")}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-[11px] uppercase tracking-wider text-white/40">
                    <tr>
                      <th className="text-start px-2 py-2">{t("shipping.id")}</th>
                      <th className="text-start px-2 py-2">{t("shipping.country")}</th>
                      <th className="text-start px-2 py-2">{t("shipping.method")}</th>
                      <th className="text-start px-2 py-2">{t("shipping.productCategory")}</th>
                      <th className="text-start px-2 py-2">{t("shipping.price")}</th>
                      <th className="text-start px-2 py-2">{t("shipping.unit")}</th>
                      <th className="text-start px-2 py-2">{t("shipping.combo")}</th>
                      <th className="text-start px-2 py-2">{t("shipping.eta")}</th>
                      <th className="text-start px-2 py-2">{t("shipping.active")}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rates.map((r, idx) => (
                      <tr key={idx} className="border-t border-white/5" data-testid={`rate-${r.id}`}>
                        <td className="px-2 py-2">
                          <input
                            type="text"
                            value={r.id}
                            onChange={(e) => updateRate(idx, { id: e.target.value })}
                            className="w-28 h-8 px-2 rounded bg-black/40 border border-white/10 text-xs"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <select
                            value={r.country}
                            onChange={(e) => applyCountryPreset(idx, e.target.value)}
                            className="w-24 h-8 px-2 rounded bg-black/40 border border-white/10 text-xs"
                          >
                            {COUNTRY_PRESETS.map((c) => <option key={c.code} value={c.code}>{c.en}</option>)}
                          </select>
                        </td>
                        <td className="px-2 py-2">
                          <select
                            value={r.method}
                            onChange={(e) => updateRate(idx, { method: e.target.value })}
                            className="w-24 h-8 px-2 rounded bg-black/40 border border-white/10 text-xs"
                          >
                            {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                          </select>
                        </td>
                        <td className="px-2 py-2">
                          <select
                            value={r.product_category || "general"}
                            onChange={(e) => updateRate(idx, { product_category: e.target.value })}
                            className="w-28 h-8 px-2 rounded bg-black/40 border border-white/10 text-xs"
                            data-testid={`rate-category-${r.id}`}
                          >
                            {PRODUCT_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                          </select>
                        </td>
                        <td className="px-2 py-2">
                          <input
                            type="number" step="0.1" min="0"
                            value={r.price_per_kg_usd}
                            onChange={(e) => updateRate(idx, { price_per_kg_usd: e.target.value })}
                            className="w-24 h-8 px-2 rounded bg-black/40 border border-white/10 text-xs"
                            data-testid={`rate-price-${r.id}`}
                          />
                        </td>
                        <td className="px-2 py-2">
                          <select
                            value={r.unit || "kg"}
                            onChange={(e) => updateRate(idx, { unit: e.target.value })}
                            className="w-32 h-8 px-2 rounded bg-black/40 border border-white/10 text-xs"
                            data-testid={`rate-unit-${r.id}`}
                          >
                            {UNITS.map((u) => <option key={u.id} value={u.id}>{u.label}</option>)}
                          </select>
                        </td>
                        <td className="px-2 py-2">
                          {/* Optional combined "+ secondary price/unit"
                              (e.g. $200/m³ + $5.5/kg). Leave price blank
                              to disable the combo for this row. */}
                          <div className="flex items-center gap-1">
                            <span className="text-[10px] text-white/40">+</span>
                            <input
                              type="number" step="0.1" min="0"
                              value={r.secondary_price_usd ?? ""}
                              placeholder="—"
                              onChange={(e) => updateRate(idx, { secondary_price_usd: e.target.value === "" ? null : Number(e.target.value) })}
                              className="w-16 h-8 px-2 rounded bg-black/40 border border-white/10 text-xs"
                              data-testid={`rate-secondary-price-${r.id}`}
                              title="Optional combined secondary price"
                            />
                            <select
                              value={r.secondary_unit || ""}
                              onChange={(e) => updateRate(idx, { secondary_unit: e.target.value || null })}
                              className="w-16 h-8 px-1 rounded bg-black/40 border border-white/10 text-xs"
                              data-testid={`rate-secondary-unit-${r.id}`}
                              title="Optional combined secondary unit"
                            >
                              <option value="">—</option>
                              <option value="kg">/kg</option>
                              <option value="cbm">/m³</option>
                            </select>
                          </div>
                        </td>
                        <td className="px-2 py-2">
                          <input
                            type="text"
                            value={r.eta || ""}
                            onChange={(e) => updateRate(idx, { eta: e.target.value })}
                            placeholder="5-7 days"
                            className="w-32 h-8 px-2 rounded bg-black/40 border border-white/10 text-xs"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <input
                            type="checkbox"
                            checked={!!r.enabled}
                            onChange={(e) => updateRate(idx, { enabled: e.target.checked })}
                          />
                        </td>
                        <td className="px-2 py-2 text-end">
                          <button
                            type="button"
                            onClick={() => setRates((rs) => rs.filter((_, i) => i !== idx))}
                            className="text-red-400/80 hover:text-red-400"
                            data-testid={`remove-rate-${r.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Save bar */}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-[#F37021] hover:bg-[#E06015] text-sm font-bold disabled:opacity-50"
              data-testid="shipping-save"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {t("shipping.saveChanges")}
            </button>
          </div>
        </div>
      )}

    </AdminShell>
  );
}

function NumberField({ label, value, onChange, testid, hint }) {
  return (
    <label className="block">
      <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-white/60">{label}</span>
      <input
        type="number" step="0.01" min="0"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1.5 w-full h-10 px-3 rounded-xl bg-black/40 border border-white/10 text-sm focus:outline-none focus:border-[#F37021]"
      />
      {hint && <span className="block text-[11px] text-white/30 mt-1">{hint}</span>}
    </label>
  );
}

export default function AdminShippingPage() {
  return (
    <RequireAdmin permission="content.manage">
      <AdminShippingPageInner />
    </RequireAdmin>
  );
}
